<template>
  <organism-escalation-events
    v-if="filterData"
    :fields="fields"
    :items="historyList"
    :filter-list="filterData"
    :escalationTemplateOptions="escalationTemplateList"
    :customerNameOptions="customerNameOption"
    :userOptions="userOptions"
    @onSetDefaultFilterData="onSetDefaultFilterData"
    @onUpdateFilterData="onUpdateFilterData"
    @onUpdateCurrentPageOrPerPage="onUpdateCurrentPageOrPerPage"
  ></organism-escalation-events>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import mixinsDatetime from "@/mixins/mixinsDatetime"
import OrganismEscalationEvents from "@/components/organisms/portal/internal/escalation/OrganismEscalationEvents.vue"
import {
  getEscalationEventList,
  getTicketEscalationActiveList,
} from "@/api/module-1/escalation"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"
import { DateTime } from "luxon"
import mixins from "@/mixins"
import { getAllUserList, getCustomerNameList } from "@/api/api-module1"

export default {
  name: "EscalationHistory",
  components: { OrganismEscalationEvents },
  mixins: [mixinsDatetime, mixinsObjectHelper, mixins],
  data() {
    return {
      fields: [
        {
          key: "templateName",
          label: "TEMPLATE NAME",
        },
        {
          key: "level",
          label: "Escalated to level",
          class: "text-nowrap",
        },
        {
          key: "escalationDate",
          label: "ESCALATION DATE",
          class: "text-nowrap",
        },
        {
          key: "issueId",
          label: "NTDC ID/Ticket ID",
          class: "text-nowrap",
        },
        {
          key: "escalateBy",
          label: "ESCALATE BY",
          class: "text-nowrap",
        },
      ],
      historyList: [],
      filterData: null,
      escalationTemplateList: null,
      customerNameOption: null,
      userOptions: null,
    }
  },

  computed: {
    ...mapGetters("appFilter", ["getInternalEscalationHistoryFilter"]),
    ...mapGetters("siteStore", ["siteOptions"]),
  },

  async mounted() {
    await this.getCustomerList()
    await this.getInternalUser()
    await this.getTicketEscalationList()

    this.cloneFilterDataFromVuex()
    await this.getEscalationEvent()
  },

  methods: {
    ...mapActions("appFilter", ["setInternalEscalationHistoryFilter"]),

    cloneFilterDataFromVuex() {
      const filterDataFromVuex = this.getInternalEscalationHistoryFilter
      this.filterData = this.deepCopyObject(filterDataFromVuex)
    },

    filterDecoration() {
      return {
        pageSize: this.filterData.perPage,
        escalationIds: this.filterData.templateName.map((item) => item.id),
        levels: this.filterData.level.map((item) => item.id),
        escalatedBys: this.filterData.escalateBy
          ? this.filterData.escalateBy.map((item) => item.username)
          : undefined,
        contractId: this.filterData.ntdcId || undefined,
        customerIds: this.filterData.customerName
          ? this.filterData.customerName.map((item) => item.id)
          : undefined,
        ticketId: this.filterData.ticketId || undefined,
        startDate: this.filterData.isEnableDate
          ? this.removeSetTimeZone(this.filterData.dateTime.start)
          : undefined,
        endDate: this.filterData.isEnableDate
          ? this.removeSetTimeZone(this.filterData.dateTime.end)
          : undefined,
        pageNumber: this.filterData.currentPage,
        customerName: this.filterData.customerName || undefined,
      }
    },

    onUpdateFilterData(updatedFilter) {
      this.setInternalEscalationHistoryFilter(updatedFilter)
      this.getEscalationEvent()
    },

    onSetDefaultFilterData() {
      const defaultFilter = {
        templateName: [],
        level: [],
        ntdcId: "",
        ticketId: "",
        escalateBy: "",
        customerName: "",
        dateTime: {
          start: DateTime.now().startOf("day").toISO(),
          end: DateTime.now().toISO(),
          now: DateTime.now().toISO(),
        },
        isEnableDate: false,
        currentPage: 1,
        totalCount: 0,
        perPage: 10,
      }
      this.setInternalEscalationHistoryFilter(defaultFilter)
      this.getEscalationEvent()
    },

    async getTicketEscalationList() {
      this.showLoading(true)
      await getTicketEscalationActiveList()
        .then(async (response) => {
          this.escalationTemplateList = await Promise.all(
            response.data.map((escalation) => {
              return {
                id: escalation.escalationId,
                name: escalation.name,
              }
            })
          )
        })
        .finally(() => this.showLoading(false))
    },

    async getCustomerList() {
      await getCustomerNameList().then((response) => {
        if (response) {
          this.customerNameOption = response.map((item) => {
            return {
              id: item.customerId,
              name: item.customerName,
            }
          })
        }
      })
    },

    async getInternalUser() {
      this.showLoading(true)
      const filter = {
        siteIds: this.siteOptions.map((site) => site.value),
      }
      await getAllUserList(filter)
        .then((response) => {
          this.userOptions = response.map((user) => ({
            username: user.username,
            name: `${user.firstName} ${user.lastName}`,
          }))
        })
        .finally(() => this.showLoading(false))
    },

    async getEscalationEvent() {
      this.showLoading(true)
      this.cloneFilterDataFromVuex()
      const params = this.filterDecoration()
      await getEscalationEventList(params)
        .then((response) => {
          this.filterData.totalCount = response.totalCount
          this.filterData.currentPage = response.currentPage
          this.setInternalEscalationHistoryFilter({
            ...this.getInternalEscalationHistoryFilter,
            totalCount: response.totalCount,
          })

          this.historyList = response?.data?.map((event) => {
            return {
              escalationId: event.escalationId,
              templateName: event.escalationName,
              level: {
                order: event.level,
                name: event.levelName,
              },
              escalationDate: {
                date: this.setDateTimeFormat(event.escalatedAt, "dd/MM/yyyy"),
                time: this.setDateTimeFormat(event.escalatedAt, "HH:mm:ss"),
              },
              issueId: {
                ntdcId:
                  event.contractId && event.contractId !== 0
                    ? event.contractId.toString().padStart(8, "0")
                    : "-",
                ticketId: event.ticketId,
              },
              escalateBy: {
                name: event?.escalatedByUser?.fullName,
                role: event?.escalatedByUser?.role?.roleName.replace(",", ", "),
              },
            }
          })
        })
        .finally(() => this.showLoading(false))
    },

    onUpdateCurrentPageOrPerPage(filter) {
      const updatedFilter = {
        ...this.getInternalEscalationHistoryFilter,
        currentPage: filter.currentPage,
        perPage: filter.perPage,
      }
      this.setInternalEscalationHistoryFilter(updatedFilter)
      this.getEscalationEvent()
    },
  },
}
</script>

<style lang="scss" scoped></style>
